import { useState, useCallback, useEffect, useRef } from 'react';

function read<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : defaultValue;
  } catch {
    return defaultValue;
  }
}

export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => read(key, defaultValue));
  const keyRef = useRef(key);

  // Persist only committed React state. Keeping storage writes out of the state
  // updater matters in Strict/Concurrent Mode, where React may replay a pure
  // updater before deciding which render to commit.
  useEffect(() => {
    if (keyRef.current !== key) {
      // A new key must be read, not overwritten with the previous key's state
      // (the lazy initializer only runs on mount).
      keyRef.current = key;
      setState(read(key, defaultValue));
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // quota exceeded, ignore
    }
    // `defaultValue` is only read on a key switch; excluding it keeps an inline
    // default object from re-running this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, state]);

  const setPersistedState = useCallback((value: T | ((prev: T) => T)) => {
    setState((previous) =>
      typeof value === 'function' ? (value as (prev: T) => T)(previous) : value
    );
  }, []);

  return [state, setPersistedState];
}
